/**
 * bh-haptic.js — haptic feedback on phones: window.bhHaptic(pattern | 'success' | 'error').
 * pattern = web-haptics format (https://haptics.lochie.me):
 *   [{ duration: 30 }, { delay: 60, duration: 40, intensity: 1 }]   (ms; intensity 0..1, default 0.5)
 *
 * Ported from web-haptics 0.0.6, which simulates intensity on BOTH platforms:
 *  - Android: navigator.vibrate with each segment PWM'd into 20ms slices (on = 20 × intensity).
 *  - iPhone (iOS 18+): no Vibration API. Each click on a hidden `<input type="checkbox" switch>`
 *    is one system tick; intensity = click density, one click every 16 + (1 − intensity) × 184 ms
 *    (intensity 1 = every frame = a buzz). First click runs inside the tap, the rest on rAF.
 * Ported rather than imported: the library loaded from jsDelivr gave no haptic on Vlad's iPhone
 * (2026-10-19), while this switch setup does.
 *
 * Only starts while the page has a live user gesture, so nothing buzzes on page load.
 * Known trade-off: Microsoft Clarity logs the hidden switch's clicks as untrusted (trust: 0): it listens
 * in the capture phase, before the stopPropagation below can act.
 */
(function () {
  var SLICE = 20, MIN_GAP = 16, GAP_RANGE = 184, MAX_MS = 1000;
  var label = null, raf = null;
  // The library's own presets (web-haptics defaultPatterns), same names and values.
  var PRESETS = {
    success: [{ duration: 30, intensity: 0.5 }, { delay: 60, duration: 40, intensity: 1 }],
    error: [{ duration: 40, intensity: 0.9 }, { delay: 40, duration: 40, intensity: 0.9 }, { delay: 40, duration: 40, intensity: 0.9 }]
  };

  function clamp(n) { return Math.max(0, Math.min(1, n)); }

  // Android: segments → navigator.vibrate([on, off, on, ...]).
  function toVibrate(segs) {
    var out = [];
    function push(on, ms) {
      if (ms <= 0) return;
      if (!out.length && !on) out.push(0);
      var lastIsOn = out.length % 2 === 1;
      if (out.length && lastIsOn === on) out[out.length - 1] += ms; else out.push(ms);
    }
    segs.forEach(function (s) {
      push(false, s.delay);
      if (s.intensity >= 1) return push(true, s.duration);
      if (s.intensity <= 0) return push(false, s.duration);
      var left = s.duration, on = Math.max(1, Math.round(SLICE * s.intensity));
      for (; left >= SLICE; left -= SLICE) { push(true, on); push(false, SLICE - on); }
      if (left > 0) { var a = Math.max(1, Math.round(left * s.intensity)); push(true, a); push(false, left - a); }
    });
    return out;
  }

  function tick() {
    if (!label) {
      label = document.createElement('label');
      label.setAttribute('aria-hidden', 'true');
      label.style.display = 'none';
      var input = document.createElement('input');
      input.type = 'checkbox';
      input.setAttribute('switch', '');
      input.tabIndex = -1;
      label.appendChild(input);
      // The synthetic click must not reach the page's own click handlers: the theme's cart
      // drawer closes on any click outside itself (app.js _onWindowClick), so on iPhone every
      // haptic closed the drawer (Vlad, 2026-10-19). Bubbling stops here; the switch still toggles.
      label.addEventListener('click', function (e) { e.stopPropagation(); });
      document.body.appendChild(label);
    }
    label.click();
  }

  // iPhone: click the switch at a density set by the current segment's intensity.
  function runTicks(segs) {
    var timeline = [], end = 0;
    segs.forEach(function (s) {
      if (s.delay) { end += s.delay; timeline.push({ end: end, on: false }); }
      end += s.duration;
      timeline.push({ end: end, on: true, gap: MIN_GAP + (1 - s.intensity) * GAP_RANGE });
    });
    var started = !segs[0].delay;
    if (started) tick(); // inside the tap
    var t0 = performance.now(), last = started ? 0 : -1; // clock starts at the tap, not the first frame
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(function frame(now) {
      var t = now - t0;
      if (t >= end) { raf = null; return; }
      var seg = timeline.find(function (x) { return t < x.end; });
      if (seg.on && (last === -1 || t - last >= seg.gap)) { tick(); last = t; }
      raf = requestAnimationFrame(frame);
    });
  }

  function normalize(pattern) {
    if (typeof pattern === 'string') pattern = PRESETS[pattern];
    return (pattern || [{ duration: 25, intensity: 0.7 }]).map(function (s) {
      return {
        delay: Math.max(0, s.delay || 0),
        duration: Math.min(MAX_MS, Math.max(0, s.duration || 0)),
        intensity: clamp(s.intensity == null ? 0.5 : s.intensity)
      };
    });
  }

  window.bhHaptic = function (pattern) {
    var ua = navigator.userActivation;
    if (ua && !ua.isActive) return;
    var segs = normalize(pattern);
    if (!segs.length) return;
    if (typeof navigator.vibrate === 'function') navigator.vibrate(toVibrate(segs));
    else runTicks(segs);
  };

  // ponytail: no test harness for theme JS; self-check behind a flag (as bh-size-guide).
  if (window.BHH_DEBUG) {
    console.assert(JSON.stringify(toVibrate(normalize([{ duration: 40, intensity: 1 }]))) === '[40]', 'full on');
    console.assert(JSON.stringify(toVibrate(normalize([{ duration: 40, intensity: 0.5 }]))) === '[10,10,10,10]', 'pwm');
    console.assert(JSON.stringify(toVibrate(normalize([{ duration: 20, intensity: 1 }, { delay: 60, duration: 20, intensity: 1 }]))) === '[20,60,20]', 'delay');
  }
})();
